import { useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { CameraWaypoint } from "../scenario/types";

interface Props {
  waypoint: CameraWaypoint | null;
  onArrived?: () => void;
  /** accessibility: collapse the tween so moves are near-instant */
  reducedMotion?: boolean;
}

const EASE: Record<string, (t: number) => number> = {
  linear: (t) => t,
  easeInOut: (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),
  easeOut: (t) => 1 - Math.pow(1 - t, 3),
  easeIn: (t) => t * t * t,
};

/**
 * Tweens the camera from its current pose to the target waypoint over the
 * waypoint's duration, then fires onArrived exactly once. Used to choreograph
 * establish -> monitor -> pull back -> phone.
 */
export function CameraRig({ waypoint, onArrived, reducedMotion }: Props) {
  const { camera } = useThree();
  const fromPos = useRef(new THREE.Vector3());
  const fromTarget = useRef(new THREE.Vector3(0, 1, 0));
  const curTarget = useRef(new THREE.Vector3(0, 1, 0));
  const fromFov = useRef(50);
  const elapsed = useRef(0);
  const activeId = useRef<string | null>(null);
  const arrived = useRef(false);

  useFrame((_, delta) => {
    if (!waypoint) return;

    // New waypoint -> capture the starting pose.
    if (activeId.current !== waypoint.id) {
      activeId.current = waypoint.id;
      elapsed.current = 0;
      arrived.current = false;
      fromPos.current.copy(camera.position);
      fromTarget.current.copy(curTarget.current);
      fromFov.current = (camera as THREE.PerspectiveCamera).fov;
    }

    const dur = reducedMotion ? 0.0001 : Math.max(0.0001, waypoint.durationS ?? 1.5);
    elapsed.current += delta;
    const tRaw = Math.min(1, elapsed.current / dur);
    const t = (EASE[waypoint.ease ?? "easeInOut"] ?? EASE.easeInOut)(tRaw);

    camera.position.lerpVectors(
      fromPos.current,
      new THREE.Vector3(...waypoint.position),
      t
    );
    curTarget.current.lerpVectors(
      fromTarget.current,
      new THREE.Vector3(...waypoint.target),
      t
    );
    camera.lookAt(curTarget.current);

    if (waypoint.fov) {
      const cam = camera as THREE.PerspectiveCamera;
      cam.fov = THREE.MathUtils.lerp(fromFov.current, waypoint.fov, t);
      cam.updateProjectionMatrix();
    }

    if (tRaw >= 1 && !arrived.current) {
      arrived.current = true;
      onArrived?.();
    }
  });

  return null;
}
